<template>
  <AdminLayout>
    <template #header>
      <router-link
        to="/admin/customer_invites/new"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition ease-in-out duration-150"
      >
        <vue-feather type="plus" class="h-4 w-4 mr-1" />
        New Invite
      </router-link>
    </template>

    <div v-if="!loadingSpinner" class="flex flex-col">
      <DataTable
        :columns="dtColumns"
        :rows="rows"
        :click-fn="viewCustomerInvite"
        :sort="sort"
        :row-actions="rowActions"
        @sort-updated="handleNewSort"
      />
    </div>

    <LoadingList />

    <Pagination :start-entry="startEntry" :end-entry="endEntry" :total="total" />

    <Modal
      v-if="showDeleteModal"
      ref="deleteModal"
      :title="'Delete Customer Invite'"
      :body="`Are you sure you want to delete the invite for ${recordToDelete?.team_name}?`"
      :action="'Delete'"
      :action-color="'red'"
      @close-modal="cancelDelete"
      @handle-action="confirmDelete"
    />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import AdminLayout from '@/components/layouts/AdminLayout/index.vue';
import LoadingList from '../shared/LoadingList.vue';
import DataTable from '../shared/DataTable/DeprecatedDataTable.vue';
import { formatCurrencyToHuman, formatStandardDateTime } from '../../utilities/format.ts';
import Pagination from '../shared/Pagination.vue';
import Modal from '../shared/Modal.vue';
import type { TableColumn, SortConfig } from '@/types/dataTable';
import type { CustomerInvite, RowAction } from '../../types/customerInvite';
import { toast } from 'vue-sonner';

const router = useRouter();
const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();

const customerInvites = ref<CustomerInvite[]>([]);
const startEntry = ref<number>(0);
const endEntry = ref<number>(0);
const total = ref<number>(0);
const page = ref<number>(route.query.page ? parseInt(route.query.page as string) : 1);
const loaded = ref<boolean>(false);
const showDeleteModal = ref<boolean>(false);
const recordToDelete = ref<CustomerInvite | null>(null);
const deleteModal = ref<InstanceType<typeof Modal> | null>(null);

watch(
  () => route.query,
  newQuery => {
    if (newQuery.page) {
      page.value = parseInt(newQuery.page as string);
    }
  }
);

const dtColumns = computed<TableColumn[]>(() => [
  {
    label: 'Team Name',
    key: 'team_name',
    sortable: true,
  },
  {
    label: 'Contact Email',
    key: 'contact_email',
    sortable: true,
  },
  {
    label: 'Stripe Plan ID',
    key: 'stripe_plan_id',
    sortable: true,
  },
  {
    label: 'Coupon',
    key: 'stripe_coupon_id',
    sortable: true,
  },
  {
    label: 'Initial Ad Spend',
    key: 'initial_ad_spend',
    formatter: (value: number | undefined) => (value ? formatCurrencyToHuman(value / 100) : 'N/A'),
    sortable: true,
  },
  {
    label: 'Team ID',
    key: 'team_id',
    formatter: (value: number | null) => value?.toString() || 'None',
    sortable: true,
  },
  {
    label: 'Invite URL',
    key: 'invite_token',
    formatter: (value: string) => `https://app.humaniz.io/invitations/${value}`,
    sortable: false,
  },
  {
    label: 'Created',
    key: 'created_at',
    formatter: formatStandardDateTime,
    sortable: true,
  },
]);

const rows = computed<CustomerInvite[]>(() => customerInvites.value);

const sort = computed<SortConfig>(() => ({
  key: (route.query.sort as string) || 'created_at',
  order: (route.query.order as 'asc' | 'desc') || 'desc',
}));

const loadingSpinner = computed<boolean>(() => loadingSpinnerStore.loadingSpinner);

const rowActions = computed<RowAction[]>(() => [
  {
    icon: 'edit-2',
    tooltip: 'Edit Invite',
    action: editCustomerInvite,
    class: 'text-blue-600 hover:text-blue-800',
  },
  {
    icon: 'trash-2',
    tooltip: 'Delete Invite',
    action: deleteCustomerInvite,
    class: 'text-red-600 hover:text-red-800',
  },
]);

async function viewCustomerInvite(record: CustomerInvite): Promise<void> {
  await router.push({ name: 'Customer Invite Admin', params: { id: record.id } });
}

async function editCustomerInvite(record: CustomerInvite): Promise<void> {
  await router.push({ name: 'Customer Invite Edit', params: { id: record.id } });
}

async function deleteCustomerInvite(record: CustomerInvite): Promise<void> {
  recordToDelete.value = record;
  showDeleteModal.value = true;
}

function cancelDelete(): void {
  showDeleteModal.value = false;
  recordToDelete.value = null;
}

async function confirmDelete(): Promise<void> {
  if (!recordToDelete.value) return;

  try {
    const response = await fetch(`/api/admin/customer_invites/${recordToDelete.value.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    });

    if (response.ok) {
      deleteModal.value?.success();
      await getCustomerInvites();
      toast.success('Invite Deleted', {
        action: { label: 'Dismiss' },
        description: `Invite for ${recordToDelete.value.team_name} was deleted successfully!`,
      });
    } else {
      deleteModal.value?.error();
      const data = await response.json();
      toast.error('Error', {
        action: { label: 'Dismiss' },
        description: data.message || 'Failed to delete customer invite',
      });
    }
  } catch (error) {
    deleteModal.value?.error();
    toast.error('Error', {
      action: { label: 'Dismiss' },
      description: 'An error occurred while deleting the customer invite',
    });
    console.error(error);
  } finally {
    recordToDelete.value = null;
  }
}

async function getCustomerInvites(): Promise<void> {
  loadingSpinnerStore.showLoadingSpinner();
  const request = await fetch(
    `/api/admin/customer_invites?page=${page.value}&sort=${sort.value.key}&order=${sort.value.order}`
  );
  const data = await request.json();
  loadingSpinnerStore.hideLoadingSpinner();
  if (data.records && Array.isArray(data.records)) {
    customerInvites.value = data.records;
  } else {
    customerInvites.value = [];
  }
  startEntry.value = data.start_entry;
  endEntry.value = data.end_entry;
  total.value = data.total;
  loaded.value = true;
}

async function handleNewSort(newSort: SortConfig): Promise<void> {
  await router.push({
    path: '/admin/customer_invites',
    query: { page: page.value, sort: newSort.key.toLowerCase(), order: newSort.order },
  });
  await getCustomerInvites();
}

onMounted(async () => {
  await getCustomerInvites();
});
</script>
